import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  segmentedGroupClass,
  segmentedButtonClass,
} from "../../dashboardStyles";

/**
 * What the chart can plot. Bookings come first because they are the figure
 * that moves weekly; accounts and businesses move by twos and threes, which a
 * chart of their own still makes visible where a shared scale would flatten
 * them to the baseline.
 */
export const GROWTH_METRICS = [
  {
    field: "newBookings",
    labelKey: "Dashboard.Platform.overview.totals.bookings",
    longKey: "Dashboard.Platform.overview.table.newBookings",
  },
  {
    field: "newUsers",
    labelKey: "Dashboard.Platform.overview.totals.users",
    longKey: "Dashboard.Platform.overview.table.newUsers",
  },
  {
    field: "newOrganizations",
    labelKey: "Dashboard.Platform.overview.totals.organizations",
    longKey: "Dashboard.Platform.overview.table.newOrganizations",
  },
];

/** A column narrower than this is not a touch target, so the plot scrolls instead. */
const MIN_COLUMN_PX = 44;

/**
 * Rounds the y-axis up to a clean top and step: a peak of 81 becomes
 * 0 / 25 / 50 / 75 / 100. These are counts, so a step is never fractional —
 * a gridline at "2.5 businesses" labels a value nothing can have.
 */
export const niceScale = (max, intervals = 4) => {
  if (!(max > 0)) {
    return {
      top: intervals,
      ticks: Array.from({ length: intervals + 1 }, (_, i) => i),
    };
  }
  const rough = max / intervals;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10]
    .map((m) => m * magnitude)
    .find((s) => s >= rough && Number.isInteger(s));
  const top = Math.ceil(max / step) * step;
  return {
    top,
    ticks: Array.from({ length: top / step + 1 }, (_, i) => i * step),
  };
};

/**
 * The month that has not finished yet. The API buckets on UTC instants and its
 * window always ends at the current UTC month, so the comparison is UTC too.
 * A part-month is drawn and tabled apart, and never compared with the month
 * before it: on the 3rd, a full September against three days of October reads
 * as a collapse that has not happened.
 */
export const isCurrentMonth = (point, now = new Date()) =>
  point.year === now.getUTCFullYear() && point.month === now.getUTCMonth() + 1;

export const InProgressBadge = () => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[0.6875rem] font-semibold text-brand-700">
      {t("Dashboard.Platform.overview.inProgress")}
    </span>
  );
};

/**
 * The change against the month before, signed by an arrow as well as a colour.
 * Nothing at all for no change: a column of "0"s would bury the months that moved.
 */
export const Delta = ({ change }) => {
  const { t, i18n } = useTranslation();
  if (!change) return null;

  const up = change > 0;
  const amount = Math.abs(change).toLocaleString(i18n.language);
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold tabular-nums ${
        up ? "text-brand-700" : "text-red-600"
      }`}
    >
      <i
        className={`bi ${up ? "bi-arrow-up-short" : "bi-arrow-down-short"} text-base leading-none`}
        aria-hidden="true"
      />
      <span aria-hidden="true">{amount}</span>
      <span className="sr-only">
        {t(
          up
            ? "Dashboard.Platform.overview.table.deltaUp"
            : "Dashboard.Platform.overview.table.deltaDown",
          { amount },
        )}
      </span>
    </span>
  );
};

Delta.propTypes = {
  change: PropTypes.number,
};

/**
 * New bookings, accounts or businesses per month, on a gridded scale.
 *
 * The readout above the plot follows the pointer (or focus, or a tap) and
 * otherwise rests on the latest month, so the value a hover reveals is never
 * the only place it exists — the table below the card carries every figure too.
 */
const PlatformGrowthChart = ({ monthly }) => {
  const { t, i18n } = useTranslation();
  const [metric, setMetric] = useState(GROWTH_METRICS[0]);
  const [active, setActive] = useState(null);

  // On a phone the twelve months do not fit and the plot scrolls. Start it at
  // the right-hand end: the recent months are the ones being looked for, and
  // a chart that opens on last November hides them off-screen.
  const scroller = useRef(null);
  useLayoutEffect(() => {
    if (scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth;
  }, [monthly]);

  const number = (n, digits = 0) =>
    n.toLocaleString(i18n.language, { maximumFractionDigits: digits });
  const date = (point, options) =>
    new Date(point.year, point.month - 1, 1).toLocaleDateString(
      i18n.language,
      options,
    );

  const values = monthly.map((point) => point[metric.field] ?? 0);
  const inProgress = monthly.map((point) => isCurrentMonth(point));
  const peak = Math.max(0, ...values);
  const { top, ticks } = niceScale(peak);
  const pct = (value) => (value / top) * 100;

  const total = values.reduce((sum, v) => sum + v, 0);
  // Averaged over finished months only, for the same reason a part-month gets
  // no delta: three days of October would drag the mean down every month.
  const finished = values.filter((_, i) => !inProgress[i]);
  const average =
    finished.length === 0
      ? null
      : finished.reduce((sum, v) => sum + v, 0) / finished.length;

  const selected = active ?? monthly.length - 1;
  const point = monthly[selected];
  const value = values[selected] ?? 0;
  const change =
    selected > 0 && !inProgress[selected]
      ? value - values[selected - 1]
      : null;

  // A number on the peak, on the latest month and on whatever is pointed at —
  // not on every cap, where twelve of them stop being read at all.
  const labelled = new Set([values.indexOf(peak), monthly.length - 1, active]);

  return (
    <div className={cardClass}>
      <div className={cardHeaderClass}>
        <h3 className={cardTitleClass}>
          {t("Dashboard.Platform.overview.growth.title")}
        </h3>
        <div
          role="group"
          aria-label={t("Dashboard.Platform.overview.growth.metricLabel")}
          className={segmentedGroupClass}
        >
          {GROWTH_METRICS.map((m) => (
            <button
              key={m.field}
              type="button"
              aria-pressed={metric.field === m.field}
              onClick={() => setMetric(m)}
              className={`${segmentedButtonClass(metric.field === m.field)} min-h-11`}
            >
              {t(m.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className={cardBodyClass}>
        {point && (
          <div className="mb-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <p className="m-0 flex flex-wrap items-center gap-2 text-xs font-medium text-ink-500">
                <span className="inline-block first-letter:uppercase">
                  {date(point, { month: "long", year: "numeric" })}
                </span>
                {inProgress[selected] && <InProgressBadge />}
              </p>
              <p className="m-0 mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-3xl font-bold text-ink-900">
                  {number(value)}
                </span>
                <span className="text-sm text-ink-500">{t(metric.longKey)}</span>
                {change !== 0 && change !== null && (
                  <span className="inline-flex items-center gap-1">
                    <Delta change={change} />
                    <span className="text-xs text-ink-500">
                      {t("Dashboard.Platform.overview.growth.vsPrevious", {
                        month: date(monthly[selected - 1], { month: "long" }),
                      })}
                    </span>
                  </span>
                )}
              </p>
            </div>

            <dl className="m-0 flex gap-8">
              <div>
                <dt className="text-xs font-medium text-ink-500">
                  {t("Dashboard.Platform.overview.growth.total")}
                </dt>
                <dd className="m-0 text-lg font-semibold text-ink-900">
                  {number(total)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-ink-500">
                  {t("Dashboard.Platform.overview.growth.average")}
                </dt>
                <dd className="m-0 text-lg font-semibold text-ink-900">
                  {average === null ? "–" : number(average, 1)}
                </dd>
              </div>
            </dl>
          </div>
        )}

        <div className="flex gap-2">
          {/* The axis stays put while a phone scrolls the months beside it. */}
          <div className="relative mt-6 h-56 w-8 shrink-0" aria-hidden="true">
            {ticks.map((tick) => (
              <span
                key={tick}
                className="absolute right-0 translate-y-1/2 text-[0.6875rem] leading-none text-ink-400 tabular-nums"
                style={{ bottom: `${pct(tick)}%` }}
              >
                {number(tick)}
              </span>
            ))}
          </div>

          <div ref={scroller} className="min-w-0 flex-1 overflow-x-auto">
            {/* pb-3 keeps a phone's overlay scrollbar off the year labels. */}
            <div
              className="pt-6 pb-3"
              style={{ minWidth: `${monthly.length * MIN_COLUMN_PX}px` }}
            >
              <div
                role="group"
                aria-label={t("Dashboard.Platform.overview.growth.chartLabel", {
                  metric: t(metric.longKey),
                })}
                className="relative h-56"
                onMouseLeave={() => setActive(null)}
              >
                <div
                  className="pointer-events-none absolute inset-0"
                  aria-hidden="true"
                >
                  {ticks.map((tick) => (
                    <div
                      key={tick}
                      className={`absolute inset-x-0 h-px ${
                        tick === 0 ? "bg-ink-300" : "bg-ink-200/70"
                      }`}
                      style={{ bottom: `${pct(tick)}%` }}
                    />
                  ))}
                </div>

                {peak === 0 && (
                  <p className="pointer-events-none absolute inset-0 m-0 flex items-center justify-center px-4 text-center text-sm text-ink-400">
                    {t("Dashboard.Platform.overview.growth.empty")}
                  </p>
                )}

                <div className="absolute inset-0 flex">
                  {monthly.map((p, i) => {
                    const isActive = active === i;
                    const barTone = inProgress[i]
                      ? isActive
                        ? "bg-brand-400"
                        : "bg-brand-300"
                      : isActive
                        ? "bg-brand-700"
                        : "bg-brand-600";
                    return (
                      <button
                        key={`${p.year}-${p.month}`}
                        type="button"
                        aria-label={t(
                          "Dashboard.Platform.overview.growth.pointLabel",
                          {
                            month: date(p, { month: "long", year: "numeric" }),
                            amount: number(values[i]),
                          },
                        )}
                        onMouseEnter={() => setActive(i)}
                        onFocus={() => setActive(i)}
                        onBlur={() => setActive(null)}
                        onClick={() => setActive(i)}
                        className={`relative flex h-full min-w-0 flex-1 cursor-pointer items-end justify-center rounded-t-md transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600 ${
                          isActive ? "bg-brand-50" : ""
                        }`}
                      >
                        {values[i] > 0 && (
                          <span
                            className={`block w-full max-w-6 rounded-t transition-colors ${barTone}`}
                            style={{ height: `max(2px, ${pct(values[i])}%)` }}
                          />
                        )}
                        {values[i] > 0 && labelled.has(i) && (
                          <span
                            className="pointer-events-none absolute left-1/2 -translate-x-1/2 pb-1 text-[0.6875rem] leading-none font-semibold whitespace-nowrap text-ink-700 tabular-nums"
                            style={{ bottom: `${pct(values[i])}%` }}
                          >
                            {number(values[i])}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-2 flex" aria-hidden="true">
                {monthly.map((p, i) => (
                  <div
                    key={`${p.year}-${p.month}`}
                    className={`min-w-0 flex-1 text-center text-[0.6875rem] leading-tight ${
                      active === i || inProgress[i]
                        ? "font-semibold text-ink-700"
                        : "text-ink-400"
                    }`}
                  >
                    <span className="block whitespace-nowrap">
                      {date(p, { month: "short" })}
                    </span>
                    {/* The year only where it changes, so twelve "26"s do
                        not crowd the axis. */}
                    <span className="block font-normal text-ink-300">
                      {i === 0 || p.month === 1 ? p.year : " "}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {inProgress.some(Boolean) && (
          <p className="m-0 mt-3 flex items-center justify-end gap-1.5 text-xs text-ink-500">
            <span className="size-2.5 rounded-sm bg-brand-300" aria-hidden="true" />
            {t("Dashboard.Platform.overview.growth.partialKey")}
          </p>
        )}
      </div>
    </div>
  );
};

PlatformGrowthChart.propTypes = {
  monthly: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
      newUsers: PropTypes.number,
      newOrganizations: PropTypes.number,
      newBookings: PropTypes.number,
    }),
  ).isRequired,
};

export default PlatformGrowthChart;
